import { hasWindow } from "@omni/utils/dom.utils";
import { isDefined } from "@omni/utils/helper.utils";
import * as LocalStorage from "@omni/utils/localStorage.utils";
import { entries, values } from "@omni/utils/object.utils";
import * as SessionStorage from "@omni/utils/sessionStorage.utils";
import { remove } from "lodash-es";

import { StorageKeys } from "@/utils/enums";

export enum GetStoreStrategy {
    DriveIn = "driveIn",
    Stock = "stock",
    Checkout = "checkout",
    Session = "session",
    Customer = "customer"
}

export enum StorePersistenceStrategy {
    Cart = "cart",
    Preferred = "preferred",
    Session = "session",
    SessionDriveIn = "sessionDriveIn",
    PreferredDriveIn = "preferredDriveIn"
}

type StoreCodeChangeListener = {
    strategy: GetStoreStrategy;
    signal?: AbortSignal;
    handler: () => void;
};

const StoreStrategies: Record<
    GetStoreStrategy,
    Array<StorePersistenceStrategy>
> = {
    [GetStoreStrategy.DriveIn]: [
        StorePersistenceStrategy.SessionDriveIn,
        StorePersistenceStrategy.PreferredDriveIn
    ],
    [GetStoreStrategy.Stock]: [
        StorePersistenceStrategy.Session,
        StorePersistenceStrategy.Cart,
        StorePersistenceStrategy.Preferred
    ],
    [GetStoreStrategy.Session]: [
        StorePersistenceStrategy.Session,
        StorePersistenceStrategy.Cart,
        StorePersistenceStrategy.Preferred
    ],
    [GetStoreStrategy.Checkout]: [
        StorePersistenceStrategy.Cart,
        StorePersistenceStrategy.Preferred
    ],
    [GetStoreStrategy.Customer]: [StorePersistenceStrategy.Preferred]
};

const StorePersistence: Record<
    StorePersistenceStrategy,
    {
        get: () => string | null;
        set: (storeCode: string) => void;
        remove: () => void;
    }
> = {
    [StorePersistenceStrategy.Session]: {
        get: () => {
            return SessionStorage.getItem(StorageKeys.PreferredStore);
        },
        set: (storeCode: string) => {
            SessionStorage.setItem(StorageKeys.PreferredStore, storeCode);
        },
        remove: () => {
            SessionStorage.removeItem(StorageKeys.PreferredStore);
        }
    },
    [StorePersistenceStrategy.SessionDriveIn]: {
        get: () => {
            return SessionStorage.getItem(StorageKeys.PreferredStoreDriveIn);
        },
        set: (storeCode: string) => {
            SessionStorage.setItem(
                StorageKeys.PreferredStoreDriveIn,
                storeCode
            );
        },
        remove: () => {
            SessionStorage.removeItem(StorageKeys.PreferredStoreDriveIn);
        }
    },
    [StorePersistenceStrategy.Cart]: {
        get: () => {
            return LocalStorage.getItem(StorageKeys.CartStoreLocalStorageKey);
        },
        set: (storeCode: string) => {
            LocalStorage.setItem(
                StorageKeys.CartStoreLocalStorageKey,
                storeCode
            );
        },
        remove: () => {
            LocalStorage.removeItem(StorageKeys.CartStoreLocalStorageKey);
        }
    },
    [StorePersistenceStrategy.Preferred]: {
        get: () => {
            return LocalStorage.getItem(
                StorageKeys.PreferredStoreLocalStorageKey
            );
        },
        set: (storeCode: string) => {
            LocalStorage.setItem(
                StorageKeys.PreferredStoreLocalStorageKey,
                storeCode
            );
        },
        remove: () => {
            LocalStorage.removeItem(StorageKeys.PreferredStoreLocalStorageKey);
        }
    },

    [StorePersistenceStrategy.PreferredDriveIn]: {
        get: () => {
            return LocalStorage.getItem(
                StorageKeys.PreferredDriveInStoreLocalStorageKey
            );
        },
        set: (storeCode: string) => {
            LocalStorage.setItem(
                StorageKeys.PreferredDriveInStoreLocalStorageKey,
                storeCode
            );
        },
        remove: () => {
            LocalStorage.removeItem(
                StorageKeys.PreferredDriveInStoreLocalStorageKey
            );
        }
    }
};

export class StoreService {
    private static storeCodeChangeListeners: StoreCodeChangeListener[] = [];

    static getStoreCode = (strategy: GetStoreStrategy) => {
        if (!hasWindow()) return undefined;

        const persistenceStrategies = StoreStrategies[strategy];

        for (const persistenceStrategy of persistenceStrategies) {
            const storeCode = StorePersistence[persistenceStrategy].get();

            if (isDefined(storeCode)) return storeCode;
        }

        return undefined;
    };

    static setStoreCode(strategy: StorePersistenceStrategy, storeCode: string) {
        if (!hasWindow()) return;
        if (!storeCode) return;

        StorePersistence[strategy].set(storeCode);

        StoreService.onStoreCodeChange(strategy);
    }

    static removeStoreCode(strategy: StorePersistenceStrategy) {
        if (!hasWindow()) return;

        StorePersistence[strategy].remove();

        StoreService.onStoreCodeChange(strategy);
    }

    static cleanUpPreferredStores() {
        values(StorePersistenceStrategy).forEach(StoreService.removeStoreCode);
    }

    static addStoreChangeListener(listener: StoreCodeChangeListener) {
        StoreService.storeCodeChangeListeners.push(listener);

        listener.signal?.addEventListener("abort", () => {
            remove(
                StoreService.storeCodeChangeListeners,
                (l) => l === listener
            );
        });
    }

    private static onStoreCodeChange(strategy: StorePersistenceStrategy) {
        const getStrategies = entries(StoreStrategies)
            .filter(([_, strategies]) => strategies.includes(strategy))
            .map(([getStoreStrategy]) => getStoreStrategy);

        StoreService.storeCodeChangeListeners
            .filter((listener) => getStrategies.includes(listener.strategy))
            .forEach((listener) => listener.handler());
    }
}
